import { slugifySeriesName } from "./slug";

export interface Crumb {
  label: string;
  path: string;
}

export function buildBreadcrumbs(
  pathname: string,
  params: Record<string, string | undefined>,
  teams: { id: string; name: string }[],
  trackWeeks: {
    id: string;
    teamId: string;
    trackName: string;
    seriesName: string | null;
    weekNumber: number | null;
    displayWeekNumber?: number | null;
  }[],
  runContext?: {
    runId: string;
    teamId: string;
    week: { id: string; trackName: string; seriesName: string | null;
            weekNumber: number | null; displayWeekNumber?: number | null } | null;
  } | null,
): Crumb[] {
  if (pathname === "/" || pathname === "/teams") {
    return [];
  }

  // #547: /runs/:id carries no team or week; the run page supplies them.
  const runMatch = pathname.match(/^\/runs\/([^/]+)/);
  if (runMatch && runContext && runContext.runId === runMatch[1]) {
    const team = teams.find((entry) => entry.id === runContext.teamId);
    const out: Crumb[] = [
      { label: "Teams", path: "/teams" },
      { label: team?.name || "Team", path: `/teams/${runContext.teamId}` },
    ];
    const week = runContext.week;
    if (week) {
      const slug = slugifySeriesName(week.seriesName);
      const wk = week.displayWeekNumber ?? week.weekNumber;
      out.push({ label: week.seriesName || unslugify(slug), path: `/teams/${runContext.teamId}/series/${slug}` });
      out.push({
        label: `${week.trackName}${wk != null ? ` (Wk ${wk})` : ""}`,
        path: `/teams/${runContext.teamId}/series/${slug}/weeks/${week.id}/overview`,
      });
    }
    out.push({ label: "Run", path: pathname });
    return out;
  }

  const crumbs: Crumb[] = [{ label: "Teams", path: "/teams" }];
  const teamId = params.teamId || extractParam(pathname, "teams");
  if (!teamId) return crumbs;

  const team = teams.find((entry) => entry.id === teamId);
  crumbs.push({ label: team?.name || "Team", path: `/teams/${teamId}` });

  const seriesSlug = params.seriesSlug || extractParam(pathname, "series");
  if (!seriesSlug) return crumbs;

  const seriesWeek =
    trackWeeks.find((week) => week.teamId === teamId && slugifySeriesName(week.seriesName) === seriesSlug) ||
    trackWeeks.find((week) => slugifySeriesName(week.seriesName) === seriesSlug);

  crumbs.push({
    label: seriesWeek?.seriesName || unslugify(seriesSlug),
    path: `/teams/${teamId}/series/${seriesSlug}`,
  });

  const trackWeekId = params.trackWeekId || extractParam(pathname, "weeks");
  if (!trackWeekId) return crumbs;

  const currentWeek = trackWeeks.find((week) => week.id === trackWeekId);
  const weekLabel = currentWeek
    ? `${currentWeek.trackName}${(currentWeek.displayWeekNumber ?? currentWeek.weekNumber) != null ? ` (Wk ${currentWeek.displayWeekNumber ?? currentWeek.weekNumber})` : ""}`
    : "Week";
  const weekBasePath = `/teams/${teamId}/series/${seriesSlug}/weeks/${trackWeekId}`;
  crumbs.push({ label: weekLabel, path: `${weekBasePath}/overview` });

  const toolMatch = pathname.match(/\/weeks\/[^/]+\/([^/]+)/);
  const tool = toolMatch?.[1];
  if (!tool || tool === "overview") return crumbs;

  const toolLabels: Record<string, string> = {
    drivers: "Drivers",
    master: "Master",
    "head-to-head": "Head-to-Head",
    fuel: "Fuel",
  };

  crumbs.push({
    label: toolLabels[tool] || unslugify(tool),
    path: `${weekBasePath}/${tool}`,
  });

  return crumbs;
}

function extractParam(pathname: string, segment: string): string | undefined {
  const parts = pathname.split("/");
  const index = parts.indexOf(segment);
  if (index >= 0 && index + 1 < parts.length) {
    return parts[index + 1];
  }
  return undefined;
}

function unslugify(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
